import { DataGrid, GridOverlay} from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import React from 'react';
import {mainInfoStorage} from '../../services/ocr/MainInfoStorageModel';
import { Box, Button, Typography } from '@mui/material';
import { Edit, Restore } from '@mui/icons-material';
import { translationService } from '../../services/translation/TranslationService';
import MainInfoEditDialog, { MainInfoPair } from './MainInfoEditDialog';
import WarningBeforeAction from '../data-components/WarningBeforeAction';

interface IMainInfoTable {
  mainInfoStorage: typeof mainInfoStorage;
  // When provided, an Edit button is shown; the edited key/value pairs are
  // handed back here (the caller batches them into the comparison save).
  onSave?: (items: MainInfoPair[]) => void;
  /**
   * When provided, a "Restore OCR output" button appears once the header differs from what OCR
   * read. The caller writes the machine values back as the stored header.
   */
  onRestore?: () => void;
}

const customOverlay = () => {
      return (
        <GridOverlay>
            <Typography variant="h6" >
              {translationService.translate("ocrService.mainInfoTable.noMainInfo")}
            </Typography>
        </GridOverlay>
      );
    }

/**
 * The inspection header: one table, the values as they currently stand, editable.
 *
 * There used to be an OCR output / Approved / Compare switch here, borrowed from the detection
 * list. A header is a handful of fields, not a list to triage: the switch made the reviewer
 * hunt for the view the Edit button lived on. Now the table always shows the current values,
 * fields a person changed are marked "(Edited by reviewer)", and the way back to what OCR read
 * is a single Restore button.
 */
export const MainInfoTable = observer(({ mainInfoStorage, onSave, onRestore }: IMainInfoTable) => {
  const [editOpen, setEditOpen] = React.useState(false);
  const [confirmRestore, setConfirmRestore] = React.useState(false);
  const pairs: MainInfoPair[] = mainInfoStorage.mainInformation.map((p) => ({
    key: p.key,
    value: p.value,
  }));

  // The table reads `rows`, which follows the source; pin it to the current values so a source
  // left over from before this switch was removed cannot leave the machine set on screen.
  React.useEffect(() => {
    if (mainInfoStorage.source !== "approved") mainInfoStorage.setSource("approved");
  }, [mainInfoStorage]);

  // Only offered when there is an OCR reading to go back to and something differs from it.
  const canRestore =
    !!onRestore &&
    mainInfoStorage.machineMainInformation.length > 0 &&
    (mainInfoStorage.reviewerChangedKeys.size > 0 ||
      mainInfoStorage.mainInformation.length !== mainInfoStorage.machineMainInformation.length);

  return (
    <div className="base-data-table">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
        <Typography variant="h6">
          {translationService.translate("ocrService.mainInfo.table-name")}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {canRestore && (
          <Button
            size="small"
            color="warning"
            startIcon={<Restore />}
            onClick={() => setConfirmRestore(true)}
            data-html2canvas-ignore
          >
            {translationService.translate("comparison.main-info.restore-ocr")}
          </Button>
        )}
        {onSave && (
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => setEditOpen(true)}
            data-html2canvas-ignore
          >
            {translationService.translate("ocrService.mainInfo.edit")}
          </Button>
        )}
      </Box>
      <DataGrid
        rows={mainInfoStorage.rows}
        columns={mainInfoStorage.columns}
        autoHeight
        hideFooter
        // The two-up key/value layout carries its own labels in the cells.
        slots={{ noRowsOverlay: customOverlay, columnHeaders: () => null }}
      />
      {onSave && (
        <MainInfoEditDialog
          open={editOpen}
          initial={pairs}
          onSave={(items) => {
            onSave(items);
            setEditOpen(false);
          }}
          onCancel={() => setEditOpen(false)}
        />
      )}
      {onRestore && (
        <WarningBeforeAction
          titleKey="comparison.main-info.restore-ocr"
          isVisible={confirmRestore}
          toggleModal={() => setConfirmRestore(false)}
          buttonOk={{
            textKey: "comparison.main-info.restore-ocr",
            action: () => {
              setConfirmRestore(false);
              onRestore();
            },
          }}
        >
          <div>{translationService.translate("comparison.main-info.restore-ocr-confirm")}</div>
        </WarningBeforeAction>
      )}
    </div>
  );
});
